import { useCallback, useEffect, useState } from "react";
import { logger } from "../lib/logger";
import { toast } from "sonner";
import {
  ChevronDown, FileDown, FileText, Filter, Heart, History as HistoryIcon, Search, Trash2,
} from "lucide-react";
import { AppShell, PageLoader } from "../components/AppShell";
import { CopyBtn } from "../components/CopyBtn";
import { ShareDropdown } from "../components/ShareDropdown";
import { MdOut } from "../components/MdOut";
import { api } from "../lib/api";
import { tplIcons } from "../components/icons";

export default function HistoryPage() {
  const [gens, setGens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  const [filterTpl, setFilterTpl] = useState("");
  const [tpls, setTpls] = useState([]);

  const fetchGens = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (filterTpl) params.set("template", filterTpl);
      const { data } = await api.get(`/generations?${params.toString()}`);
      setGens(data);
    } catch (err) {
      logger.error("Failed to fetch history", err);
    }
  }, [search, filterTpl]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/templates");
        setTpls(data);
      } catch (err) {
        logger.error("Failed to load templates", err);
      }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchGens().then(() => setLoading(false));
  }, [fetchGens]);

  const toggleFavorite = async (id) => {
    try {
      const { data } = await api.post(`/generations/${id}/favorite`);
      setGens((prev) => prev.map((g) => (g.id === id ? { ...g, is_favorite: data.is_favorite } : g)));
      toast.success(data.is_favorite ? "Favorited" : "Unfavorited");
    } catch (err) {
      logger.error("Toggle favorite failed", err);
      toast.error("Failed to update favorite");
    }
  };

  const deleteGen = async (id) => {
    try {
      await api.delete(`/generations/${id}`);
      setGens((prev) => prev.filter((g) => g.id !== id));
      toast.success("Deleted");
    } catch (err) {
      logger.error("Delete generation failed", err);
      toast.error("Failed to delete");
    }
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get("/generations/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "copyforge_history.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported!");
    } catch (err) {
      logger.error("CSV export failed", err);
      toast.error("Export failed");
    }
  };

  if (loading) return <PageLoader />;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="history-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-black tracking-tighter text-zinc-950 dark:text-white font-heading">History</h1>
          <div className="flex items-center gap-3">
            <span className="text-sm text-zinc-400">{gens.length} items</span>
            <button onClick={exportCsv} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-brand transition-colors" data-testid="export-csv-btn">
              <FileDown className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} className="w-full bg-white border border-zinc-200 pl-10 pr-4 py-2.5 text-sm text-zinc-900 focus:border-brand focus:ring-1 focus:ring-brand outline-none" placeholder="Search by topic..." data-testid="history-search-input" />
          </div>
          <div className="relative">
            <Filter className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <select value={filterTpl} onChange={(e) => setFilterTpl(e.target.value)} className="bg-white border border-zinc-200 pl-10 pr-8 py-2.5 text-sm text-zinc-900 focus:border-brand outline-none appearance-none" data-testid="history-filter-select">
              <option value="">All Templates</option>
              {tpls.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
        {gens.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-history">
            <HistoryIcon className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">{search || filterTpl ? "No results found." : "No generations yet."}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {gens.map((g) => {
              const Icon = tplIcons[g.template] || FileText;
              const isExpanded = expanded === g.id;
              return (
                <div key={g.id} className="bg-white border border-zinc-200 hover:border-zinc-300" data-testid={`history-item-${g.id}`}>
                  <div className="flex items-center gap-4 p-4">
                    <div className="w-10 h-10 bg-brand/5 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-brand" />
                    </div>
                    <button onClick={() => setExpanded(isExpanded ? null : g.id)} className="flex-1 min-w-0 text-left" data-testid={`history-toggle-${g.id}`}>
                      <p className="font-semibold text-zinc-900 text-sm truncate">{g.topic}</p>
                      <p className="text-xs text-zinc-500">{g.template_name} &middot; {g.word_count || 0} words {g.refined && <span className="text-brand">&middot; Refined</span>}</p>
                    </button>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button onClick={() => toggleFavorite(g.id)} className="p-1.5 hover:bg-zinc-100" data-testid={`fav-btn-${g.id}`}>
                        <Heart className={`w-4 h-4 ${g.is_favorite ? "fill-red-500 text-red-500" : "text-zinc-300"}`} />
                      </button>
                      <button onClick={() => deleteGen(g.id)} className="p-1.5 hover:bg-red-50 text-zinc-300 hover:text-red-500" data-testid={`delete-btn-${g.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <span className="text-xs text-zinc-400 ml-1">{new Date(g.created_at).toLocaleDateString()}</span>
                      <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform cursor-pointer ${isExpanded ? "rotate-180" : ""}`} onClick={() => setExpanded(isExpanded ? null : g.id)} />
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-zinc-100 p-5 animate-fade-in" data-testid={`history-content-${g.id}`}>
                      <div className="flex justify-end mb-3 gap-2">
                        <CopyBtn text={g.output} label="Copy" />
                        <ShareDropdown text={g.output} title={g.topic} />
                      </div>
                      <MdOut content={g.output} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
